/**
 * Destination detection constants and helpers.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 *
 * All functions are pure — no closure dependencies, no dynamic caches.
 */

// Used by isHotelOrResort's isShortCitySuffix guard so we only treat
// "Hotel <token>" as a city when <token> is actually a known city. Otherwise
// generic 2-token hotel names like "Hotel California" / "Hotel Plaza" /
// "Hotel Roma" stay classified as hotels.
import { KNOWN_TRAVEL_CITIES } from './extractionPostProcess.js';

// ─── String distance ────────────────────────────────────────────────────────

export const levenshteinDistance = (str1, str2) => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  const len1 = s1.length;
  const len2 = s2.length;

  // Quick checks
  if (len1 === 0) return len2;
  if (len2 === 0) return len1;
  if (s1 === s2) return 0;

  // Create distance matrix
  const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(null));

  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,     // deletion
        matrix[i][j - 1] + 1,     // insertion
        matrix[i - 1][j - 1] + cost  // substitution
      );
    }
  }

  return matrix[len1][len2];
};

// ─── Known location lists ───────────────────────────────────────────────────

export const DESTINATION_CITIES = [
  'Paris', 'London', 'Tokyo', 'Rome', 'Barcelona', 'Amsterdam', 'Berlin', 'Madrid',
  'Vienna', 'Prague', 'Budapest', 'Lisbon', 'Dublin', 'Edinburgh', 'Copenhagen',
  'Stockholm', 'Oslo', 'Helsinki', 'Athens', 'Istanbul', 'Dubai', 'Singapore',
  'Hong Kong', 'Bangkok', 'Bali', 'Sydney', 'Melbourne', 'Auckland', 'Vancouver',
  'Toronto', 'Montreal', 'New York', 'Los Angeles', 'San Francisco', 'Chicago',
  'Miami', 'Las Vegas', 'Seattle', 'Boston', 'Denver', 'Austin', 'Nashville',
  'Mexico City', 'Cancun', 'Buenos Aires', 'Rio de Janeiro', 'Lima', 'Bogota',
  'Cartagena', 'Reykjavik', 'Cairo', 'Marrakech', 'Cape Town', 'Nairobi',
  'Santorini', 'Mykonos', 'Florence', 'Venice', 'Milan', 'Naples', 'Cinque Terre',
  'Amalfi', 'Nice', 'Lyon', 'Bordeaux', 'Provence', 'Munich', 'Hamburg', 'Cologne',
  'Salzburg', 'Interlaken', 'Zurich', 'Geneva', 'Brussels', 'Bruges', 'Krakow',
  'Warsaw', 'Split', 'Dubrovnik', 'Ljubljana', 'Seville', 'Valencia', 'Granada',
  'Malaga', 'Porto', 'Seoul', 'Osaka', 'Kyoto', 'Taipei', 'Beijing', 'Shanghai',
  'Hanoi', 'Ho Chi Minh', 'Phuket', 'Chiang Mai', 'Kuala Lumpur', 'Jakarta',
  'Mumbai', 'Delhi', 'Jaipur', 'Agra', 'Goa', 'Hawaii', 'Maui', 'Fiji', 'Maldives',
  'Mauritius', 'Seychelles', 'Patagonia', 'Cusco', 'Machu Picchu', 'Galapagos',
  'Costa Rica', 'Puerto Rico', 'Bahamas', 'Jamaica', 'Turks and Caicos',
  'Zermatt', 'Lucerne', 'Queenstown', 'Aspen', 'Whistler', 'Chamonix',
  // Additional cities
  'Taipei', 'Manila', 'Cebu', 'Bora Bora', 'Tahiti', 'Petra', 'Amman', 'Tel Aviv',
  'Jerusalem', 'Casablanca', 'Fez', 'Zanzibar', 'Victoria Falls', 'Kruger',
  'Serengeti', 'Masai Mara', 'Kilimanjaro', 'Mombasa', 'Luxor', 'Aswan',
  'Havana', 'Punta Cana', 'Aruba', 'Curacao', 'St Lucia', 'Barbados', 'Bermuda',
  'Tulum', 'Playa del Carmen', 'Cabo', 'Puerto Vallarta', 'Oaxaca', 'Guadalajara',
  'Medellin', 'Montevideo', 'Santiago', 'Valparaiso', 'Mendoza', 'Ushuaia',
  'Quito', 'Cartagena', 'San Juan', 'Nassau', 'Grand Cayman', 'Cozumel',
  'Antigua', 'Granada Nicaragua', 'Panama City', 'San Jose', 'Belize City',
  'Ho Chi Minh City', 'Saigon', 'Da Nang', 'Hoi An', 'Siem Reap', 'Angkor Wat',
  'Luang Prabang', 'Vientiane', 'Yangon', 'Mandalay', 'Kathmandu', 'Pokhara',
  'Colombo', 'Kandy', 'Galle', 'Bhutan', 'Thimphu', 'Lhasa', 'Tibet',
  'Xi\'an', 'Guilin', 'Chengdu', 'Hangzhou', 'Suzhou', 'Nanjing', 'Shenzhen',
  'Macau', 'Jeju', 'Busan', 'Nara', 'Hiroshima', 'Hokkaido', 'Sapporo', 'Okinawa',
  'Uluru', 'Great Barrier Reef', 'Cairns', 'Gold Coast', 'Perth', 'Adelaide', 'Tasmania',
  'Rotorua', 'Wellington', 'Christchurch', 'Milford Sound', 'Hobbiton',
  'Moscow', 'St Petersburg', 'Tallinn', 'Riga', 'Vilnius', 'Minsk', 'Kiev', 'Kyiv',
  'Bucharest', 'Sofia', 'Belgrade', 'Sarajevo', 'Tirana', 'Skopje', 'Podgorica',
  'Zagreb', 'Bratislava', 'Luxembourg', 'Monaco', 'San Marino', 'Andorra',
  'Canary Islands', 'Tenerife', 'Ibiza', 'Mallorca', 'Menorca', 'Corsica', 'Sardinia',
  'Sicily', 'Capri', 'Positano', 'Ravello', 'Sorrento', 'Portofino', 'Lake Como',
  'Lake Garda', 'Verona', 'Bologna', 'Pisa', 'Siena', 'Tuscany', 'Umbria',
  'Crete', 'Rhodes', 'Corfu', 'Zakynthos', 'Meteora', 'Delphi', 'Thessaloniki',
  'Antalya', 'Cappadocia', 'Bodrum', 'Ephesus', 'Pamukkale', 'Fethiye',
  'Scottish Highlands', 'Lake District', 'Cotswolds', 'Bath', 'Oxford', 'Cambridge',
  'York', 'Liverpool', 'Manchester', 'Bristol', 'Cardiff', 'Belfast', 'Galway', 'Cork',
  'Normandy', 'Loire Valley', 'Champagne', 'Alsace', 'Dordogne', 'French Riviera',
  'Marseille', 'Montpellier', 'Toulouse', 'Strasbourg', 'Avignon',
];

// Known countries for destination detection
export const KNOWN_COUNTRIES = [
  'Japan', 'France', 'Italy', 'Spain', 'Portugal', 'Greece', 'Turkey', 'Croatia',
  'Germany', 'Austria', 'Switzerland', 'Netherlands', 'Belgium', 'Denmark', 'Sweden',
  'Norway', 'Finland', 'Iceland', 'Ireland', 'Scotland', 'England', 'Wales', 'UK',
  'United Kingdom', 'Poland', 'Czech Republic', 'Hungary', 'Romania', 'Bulgaria',
  'Slovenia', 'Slovakia', 'Serbia', 'Montenegro', 'Albania', 'North Macedonia',
  'Bosnia', 'Kosovo', 'Moldova', 'Ukraine', 'Belarus', 'Russia', 'Estonia', 'Latvia',
  'Lithuania', 'Georgia', 'Armenia', 'Azerbaijan', 'Kazakhstan',
  'Cyprus', 'Malta', 'Luxembourg', 'Monaco', 'Liechtenstein', 'Andorra', 'San Marino',
  'Thailand', 'Vietnam', 'Cambodia', 'Laos', 'Myanmar', 'Malaysia', 'Indonesia',
  'Philippines', 'China', 'Taiwan', 'South Korea', 'Korea', 'India', 'Nepal',
  'Sri Lanka', 'Bhutan', 'Bangladesh', 'Pakistan', 'Mongolia', 'Singapore', 'Hong Kong',
  'Maldives',
  'Australia', 'New Zealand', 'Fiji', 'Tahiti', 'Samoa', 'Tonga', 'Vanuatu',
  'Morocco', 'Egypt', 'South Africa', 'Kenya', 'Tanzania', 'Rwanda', 'Uganda',
  'Ethiopia', 'Namibia', 'Botswana', 'Zimbabwe', 'Zambia', 'Mozambique', 'Madagascar',
  'Mauritius', 'Seychelles', 'Tunisia', 'Algeria', 'Senegal', 'Ghana', 'Nigeria',
  'USA', 'United States', 'America', 'Canada', 'Mexico', 'Guatemala', 'Belize',
  'Honduras', 'El Salvador', 'Nicaragua', 'Costa Rica', 'Panama',
  'Cuba', 'Jamaica', 'Haiti', 'Dominican Republic', 'Puerto Rico', 'Bahamas',
  'Trinidad', 'Trinidad and Tobago', 'Barbados', 'St Lucia', 'Grenada', 'Antigua',
  'St Kitts', 'Aruba', 'Curacao', 'Cayman Islands', 'Turks and Caicos', 'Bermuda',
  'Colombia', 'Ecuador', 'Peru', 'Bolivia', 'Chile', 'Argentina', 'Uruguay',
  'Paraguay', 'Brazil', 'Venezuela', 'Guyana', 'Suriname',
  'Israel', 'Jordan', 'Lebanon', 'UAE', 'Oman', 'Qatar', 'Bahrain', 'Kuwait',
  'Saudi Arabia', 'Iran', 'Iraq',
];

// Lowercase Set for O(1) lookups — derived from KNOWN_COUNTRIES
export const KNOWN_COUNTRIES_LOWER = new Set(KNOWN_COUNTRIES.map(c => c.toLowerCase()));

// Known regions for destination detection
export const KNOWN_REGIONS = [
  'Europe', 'Asia', 'Africa', 'South America', 'North America', 'Central America',
  'Caribbean', 'Middle East', 'Southeast Asia', 'East Asia', 'South Asia',
  'Oceania', 'Pacific Islands', 'Scandinavia', 'Balkans', 'Baltic States',
  'Mediterranean', 'Alps', 'Pyrenees', 'Dolomites', 'Riviera', 'Adriatic',
  'Aegean', 'Black Sea', 'Patagonia', 'Amazon', 'Andes', 'Himalayas',
  'Sahara', 'Serengeti', 'Masai Mara', 'Kruger', 'Okavango', 'Victoria Falls',
  'Great Barrier Reef', 'Outback', 'South Island', 'North Island',
  'New England', 'Pacific Northwest', 'Southwest', 'Midwest', 'Deep South',
  'Yucatan', 'Riviera Maya', 'Baja California', 'Wine Country', 'Napa Valley',
  'Tuscany', 'Provence', 'Andalusia', 'Catalonia', 'Basque Country', 'Galicia',
  'Bavaria', 'Black Forest', 'Rhine Valley', 'Mosel Valley',
  'Scottish Highlands', 'Lake District', 'Cotswolds', 'Cornwall', 'Yorkshire',
  'Loire Valley', 'Champagne', 'Burgundy', 'Alsace', 'Normandy', 'Brittany',
  'Amalfi Coast', 'Cinque Terre', 'Lake Como', 'Umbria', 'Puglia', 'Sardinia',
  'Greek Islands', 'Cyclades', 'Dodecanese', 'Ionian Islands',
  'Dalmatian Coast', 'Istria', 'Montenegro Coast',
];

// ─── Compound place names ───────────────────────────────────────────────────

// Compound place names that contain "and" or "&" and must NOT be split
export const COMPOUND_PLACE_NAMES = [
  'turks and caicos', 'trinidad and tobago', 'antigua and barbuda',
  'st kitts and nevis', 'saint kitts and nevis', 'bosnia and herzegovina',
  'sao tome and principe', 'são tomé and príncipe',
  'saint vincent and the grenadines', 'st vincent and the grenadines',
  'wallis and futuna', 'lewis and harris',
  'north and south islands', 'east and west coast',
  'arts and culture', 'art and culture', 'food and wine', 'sea and sand',
];

// Protect compound place names before splitting on "and"/"&"
// Returns { text, placeholders } — call restoreCompoundNames after splitting
export const protectCompoundNames = (text) => {
  const placeholders = [];
  let result = text;
  for (const name of COMPOUND_PLACE_NAMES) {
    // Build regex that matches "and" or "&" between the words
    const escaped = name.replace(/\s+and\s+/g, '\\s+(?:and|&)\\s+');
    const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
    result = result.replace(regex, (match) => {
      placeholders.push(match);
      return `__COMPOUND_${placeholders.length - 1}__`;
    });
  }
  return { text: result, placeholders };
};

// Restore compound names in an array of split parts
export const restoreCompoundNames = (parts, placeholders) => {
  return parts.map(p => p.replace(/__COMPOUND_(\d+)__/g, (_, idx) => placeholders[parseInt(idx)]));
};

// ─── Hotels & resorts ───────────────────────────────────────────────────────

// Known hotels, resorts, and property brands for destination detection
// Users often plan trips around specific hotels/resorts
export const KNOWN_HOTELS_RESORTS = [
  // Luxury hotel brands
  'Four Seasons', 'Ritz Carlton', 'The Ritz', 'Mandarin Oriental', 'St Regis',
  'Park Hyatt', 'Waldorf Astoria', 'Peninsula', 'Rosewood', 'Aman',
  'Amangiri', 'Amanpuri', 'Amanyara', 'Amanemu', 'Amankora', 'Amanzoe',
  'Six Senses', 'One&Only', 'Belmond', 'Raffles', 'Fairmont', 'Sofitel',
  'Conrad', 'W Hotel', 'W Hotels', 'JW Marriott', 'Edition', 'The Edition',
  'Bulgari Hotel', 'Bulgari Resort', 'Armani Hotel', 'Armani Hotels',
  'Oberoi', 'Taj', 'Leela', 'ITC Hotels', 'Banyan Tree', 'Anantara',
  'Como Hotels', 'Oetker Collection', 'Dorchester Collection',
  // Iconic individual properties
  'Burj Al Arab', 'Emirates Palace', 'Atlantis The Palm', 'Atlantis Dubai',
  'Atlantis Bahamas', 'Atlantis Paradise Island', 'The Plaza', 'The Savoy',
  'Claridges', 'The Dorchester', 'The Connaught', 'Hotel & The Ned', 'The Ned',
  'The Ritz Paris', 'Plaza Athenee', 'Le Bristol', 'Le Meurice', 'Hotel Costes',
  'Chateau Marmont', 'The Beverly Hills Hotel', 'Hotel Bel-Air', 'The Standard',
  'Ace Hotel', 'Soho House', 'The Hoxton', 'Nobu Hotel', 'Edition Hotels',
  'The Carlyle', 'Pierre Hotel', 'The Mark', 'The Greenwich Hotel',
  'Post Ranch Inn', 'Ventana Big Sur', 'Amangiri Utah',
  'Singita', 'AndBeyond', 'Wilderness Safaris', 'Great Plains Conservation',
  // All-inclusive resorts
  'Sandals', 'Beaches', 'Club Med', 'Secrets Resorts', 'Dreams Resorts',
  'Excellence Resorts', 'Zoetry', 'Breathless', 'Now Resorts', 'Riu',
  'Iberostar', 'Paradisus', 'Live Aqua', 'Grand Velas', 'Hard Rock Hotel',
  'Moon Palace', 'Palace Resorts', 'Karisma Hotels', 'El Dorado Spa Resorts',
  // Maldives resorts
  'Soneva Fushi', 'Soneva Jani', 'Gili Lankanfushi', 'Cheval Blanc Randheli',
  'St Regis Maldives', 'Waldorf Astoria Maldives', 'Patina Maldives',
  'The Nautilus Maldives', 'Velaa Private Island', 'Milaidhoo', 'Joali',
  'Kudadoo', 'Niyama', 'Huvafen Fushi', 'Conrad Maldives', 'Anantara Kihavah',
  // Caribbean resorts
  'Eden Rock', 'Le Sirenuse', 'Sugar Beach', 'Jade Mountain', 'Ladera',
  'Curtain Bluff', 'Jumby Bay', 'Peter Island', 'Necker Island', 'Petit St Vincent',
  'Caneel Bay', 'Little Dix Bay', 'Round Hill', 'Half Moon', 'Tryall Club',
  // Asian resorts
  'Amanpulo', 'El Nido Resorts', 'Pangulasian Island', 'Miniloc Island',
  'Song Saa', 'Shinta Mani', 'Amansara', 'Raffles Grand Hotel d\'Angkor',
  'The Siam', 'Mandarin Oriental Bangkok', 'Peninsula Bangkok', 'Capella Bangkok',
  'Aman Tokyo', 'Park Hyatt Tokyo', 'Hoshinoya', 'Beniya Mukayu',
  'Gora Kadan', 'Asaba', 'Kayotei', 'Tawaraya', 'Hiiragiya',
  // Ski resorts
  'The Little Nell', 'St Regis Aspen', 'Limelight Hotel', 'The Sebastian',
  'Four Seasons Vail', 'The Arrabelle', 'Montage Deer Valley', 'Stein Eriksen Lodge',
  'The Chedi Andermatt', 'The Alpina Gstaad', 'Grand Hotel Zermatterhof',
  'Badrutt\'s Palace', 'Kulm Hotel', 'Carlton Hotel St Moritz',
  // Disney and theme park resorts
  'Disney Grand Floridian', 'Disney Contemporary', 'Disney Polynesian',
  'Disney Beach Club', 'Disney Yacht Club', 'Disney Wilderness Lodge',
  'Disney Grand Californian', 'Disneyland Hotel', 'Disney Riviera',
  'Universal Loews', 'Cabana Bay', 'Hard Rock Orlando',
  // Cruise lines (often booked as destinations)
  'Regent Seven Seas', 'Silversea', 'Seabourn', 'Crystal Cruises',
  'Viking Ocean', 'Oceania', 'Azamara', 'Windstar', 'Paul Gauguin',
  'Ponant', 'Scenic', 'Ritz Carlton Yacht', 'Four Seasons Yacht',
  // Wellness and spa resorts
  'Canyon Ranch', 'Miraval', 'Cal-a-Vie', 'Golden Door', 'Lake Austin Spa',
  'Rancho La Puerta', 'SHA Wellness', 'Chiva-Som', 'Kamalaya',
  'COMO Shambhala', 'Vana', 'Ananda', 'The Farm at San Benito',
];

// Common hotel/resort keywords to help detect property names not in our list
// NOTE: "chateau" removed because it's often a wine region (Châteauneuf-du-Pape, Château Margaux, etc.)
export const HOTEL_RESORT_KEYWORDS = [
  'hotel', 'hotels', 'resort', 'resorts', 'inn', 'lodge', 'villa', 'villas',
  'palace', 'castle', 'manor', 'house', 'spa', 'retreat',
  'sanctuary', 'hideaway', 'collection', 'residences', 'suites',
];

// Patterns that indicate the user is ASKING FOR a recommendation, not naming a specific place
// e.g., "luxury hotel in Tokyo" or "best resort in Maldives" - they want suggestions, not booking a named hotel
export const HOTEL_RECOMMENDATION_PATTERNS = [
  /\b(best|top|nicest|luxury|boutique|affordable|cheap|budget|good|great|amazing)\s+(hotel|resort|inn|lodge|villa)s?\s+(in|near|around|at)\b/i,
  /\b(hotel|resort|inn|lodge|villa)s?\s+(in|near|around|at)\s+/i,
  /\b(recommend|suggest|find|show)\s+(a|me|us|some)?\s*(hotel|resort|inn|lodge|villa|place to stay)s?\b/i,
  /\bwhere\s+(should|can|could|to)\s+(i|we)?\s*stay\b/i,
  /\b(looking for|need)\s+(a|some)?\s*(hotel|resort|place to stay)\b/i,
  /\bi\s+want\s+(a|an)\s+\w+\s+(hotel|resort|inn|lodge|villa|place|stay)\b/i,
  /\bi\s+(?:want|need|prefer)\s+(a|an)\s+(hotel|resort|inn|lodge|villa)\b/i,
  // "hotel with a pool", "resort with ocean view", "hotel that has..."
  /\b(hotel|resort|inn|lodge|villa)\s+(with|that\s+has|that\s+is|overlooking|facing|near the)\b/i,
  // "somewhere to stay", "place to stay", "somewhere nice to stay"
  /\b(somewhere|someplace|place)\s+(?:\w+\s+)?to\s+stay\b/i,
  // "hotel options", "hotel suggestions", "hotel ideas", "hotel recs"
  /\b(hotel|resort|villa|lodge)\s+(options|suggestions|ideas|recs|recommendations)\b/i,
  // "any hotels", "any good hotels", "know any hotels"
  /\bany\s+(?:\w+\s+)?(hotel|resort|inn|lodge|villa)s?\b/i,
  // "stay somewhere", "stay at a", "stay in a"
  /\bstay\s+(somewhere|at\s+a|in\s+a)\b/i,
  // "not too expensive", "nothing too fancy" (follow-up preferences about accommodation)
  /\b(something|nothing)\s+(too\s+)?(expensive|fancy|basic|cheap|far|central|walkable)\b/i,
  // "with a view", "with a pool", "with a rooftop" (follow-up describing hotel features)
  /\bwith\s+(a\s+)?(view|pool|rooftop|balcony|spa|gym|terrace|garden|jacuzzi|kitchen|breakfast)\b/i,
];

// Quality adjectives that indicate generic requests (not specific names)
export const QUALITY_ADJECTIVES = [
  'nice', 'good', 'great', 'best', 'top', 'luxury', 'luxurious',
  'boutique', 'affordable', 'cheap', 'budget', 'fancy', 'beautiful',
  'romantic', 'quiet', 'peaceful', 'cozy', 'modern', 'historic',
  'beachfront', 'oceanfront', 'waterfront', 'scenic', 'amazing',
  'perfect', 'ideal', 'decent', 'reasonable', 'comfortable',
  'small', 'big', 'large', 'tiny', 'charming', 'trendy', 'stylish',
  'unique', 'cool', 'cute', 'hip', 'chic', 'upscale', 'elegant',
  'intimate', 'quaint', 'rustic', 'central', 'downtown',
];

// Generic accommodation words (not brand names)
export const GENERIC_ACCOMMODATION_WORDS = [
  'hotel', 'resort', 'inn', 'lodge', 'villa', 'place', 'spot',
  'accommodation', 'stay', 'motel', 'hostel', 'airbnb', 'rental',
  'property', 'room', 'suite'
];

// Pattern to detect generic accommodation requests like "a nice hotel", "some good resort"
export const GENERIC_ACCOMMODATION_PATTERN = new RegExp(
  `\\b(a|an|some|any)\\s+(?:(?:really|very|quite|pretty|super)\\s+)?(?:(${QUALITY_ADJECTIVES.join('|')})\\s+)+(?:\\w+\\s+)?(${GENERIC_ACCOMMODATION_WORDS.join('|')})s?\\b`,
  'i'
);

// ─── Hotel location mapping ─────────────────────────────────────────────────

// Extract actual geographic location from hotel/resort names for itinerary generation
export const HOTEL_LOCATION_MAP = {
  // Aman resorts
  'amangiri': 'Utah',
  'aman utah': 'Utah',
  'aman venice': 'Venice, Italy',
  'amanpuri': 'Phuket, Thailand',
  'amanyara': 'Turks and Caicos',
  'amanemu': 'Mie Prefecture, Japan',
  'amanzoe': 'Porto Heli, Greece',
  'amankora': 'Bhutan',
  'amankila': 'Bali, Indonesia',
  'amanjiwo': 'Java, Indonesia',
  'aman tokyo': 'Tokyo, Japan',
  'aman new york': 'New York City',
  'aman kyoto': 'Kyoto, Japan',
  // Wellness resorts
  'canyon ranch': 'Tucson, Arizona',
  'canyon ranch lenox': 'Lenox, Massachusetts',
  'canyon ranch tucson': 'Tucson, Arizona',
  'miraval': 'Tucson, Arizona',
  'miraval arizona': 'Tucson, Arizona',
  'miraval austin': 'Austin, Texas',
  'miraval berkshires': 'Lenox, Massachusetts',
  'blackberry farm': 'Walland, Tennessee',
  'blackberry mountain': 'Walland, Tennessee',
  'the ranch malibu': 'Malibu, California',
  'cal-a-vie': 'Vista, California',
  'golden door': 'San Marcos, California',
  'lake austin spa': 'Austin, Texas',
  'rancho la puerta': 'Tecate, Mexico',
  // East Coast resorts
  'salamander resort': 'Middleburg, Virginia',
  'mohonk mountain house': 'New Paltz, New York',
  'whiteface lodge': 'Lake Placid, New York',
  'mirror lake inn': 'Lake Placid, New York',
  'lake placid lodge': 'Lake Placid, New York',
  'chatham bars inn': 'Chatham, Cape Cod',
  'mirbeau inn': 'Skaneateles, New York',
  'ocean house': 'Watch Hill, Rhode Island',
  'weekapaug inn': 'Westerly, Rhode Island',
  'wheatleigh': 'Lenox, Massachusetts',
  'blantyre': 'Lenox, Massachusetts',
  'the pitcher inn': 'Warren, Vermont',
  'twin farms': 'Barnard, Vermont',
  // Caribbean & Islands
  'jade mountain': 'St. Lucia',
  'sugar beach': 'St. Lucia',
  'ladera resort': 'St. Lucia',
  'cap juluca': 'Anguilla',
  'belmond cap juluca': 'Anguilla',
  'eden rock': 'St. Barths',
  'le sereno': 'St. Barths',
  'cheval blanc randheli': 'Noonu Atoll, Maldives',
  'cheval blanc st-barth': 'St. Barths',
  'cheval blanc': 'St. Barths',
  'parrot cay': 'Turks and Caicos',
  'como parrot cay': 'Turks and Caicos',
  'grace bay club': 'Turks and Caicos',
  // International — Safari lodges (BUG 11 FIX: specific entries BEFORE generic brand)
  'singita sabi sand': 'Sabi Sand Game Reserve, South Africa',
  'singita sabi': 'Sabi Sand Game Reserve, South Africa',
  'singita ebony': 'Sabi Sand Game Reserve, South Africa',
  'singita boulders': 'Sabi Sand Game Reserve, South Africa',
  'singita castleton': 'Sabi Sand Game Reserve, South Africa',
  'singita lebombo': 'Kruger National Park, South Africa',
  'singita sweni': 'Kruger National Park, South Africa',
  'singita kruger': 'Kruger National Park, South Africa',
  'singita grumeti': 'Serengeti, Tanzania',
  'singita faru faru': 'Serengeti, Tanzania',
  'singita sasakwa': 'Serengeti, Tanzania',
  'singita sabora': 'Serengeti, Tanzania',
  'singita kwitonda': 'Volcanoes National Park, Rwanda',
  'singita': 'Tanzania/South Africa',
  'anantara bazaruto': 'Bazaruto Island, Mozambique',
  'anantara medjumbe': 'Quirimbas, Mozambique',
  'anantara vilanculos': 'Vilanculos, Mozambique',
  '&beyond': 'South Africa',
  '&beyond phinda': 'KwaZulu-Natal, South Africa',
  '&beyond kirkman': 'Sabi Sand Game Reserve, South Africa',
  '&beyond ngala': 'Kruger National Park, South Africa',
  'londolozi': 'Sabi Sand Game Reserve, South Africa',
  'lion sands': 'Sabi Sand Game Reserve, South Africa',
  'royal malewane': 'Greater Kruger, South Africa',
  'sabi sabi': 'Sabi Sand Game Reserve, South Africa',
  'angama mara': 'Masai Mara, Kenya',
  'cottar': 'Masai Mara, Kenya',
  'mahali mzuri': 'Masai Mara, Kenya',
  'giraffe manor': 'Nairobi, Kenya',
  'soneva fushi': 'Baa Atoll, Maldives',
  'soneva jani': 'Noonu Atoll, Maldives',
  'four seasons maldives': 'North Malé Atoll, Maldives',
  'one&only reethi rah': 'North Malé Atoll, Maldives',
  'como cocoa island': 'South Malé Atoll, Maldives',
  'centara ras fushi': 'North Malé Atoll, Maldives',
  'centara ras fushi resort': 'North Malé Atoll, Maldives',
  'centara grand island': 'South Ari Atoll, Maldives',
  'centara grand maldives': 'South Ari Atoll, Maldives',
  'anantara kihavah': 'Baa Atoll, Maldives',
  'anantara veli': 'South Malé Atoll, Maldives',
  'anantara dhigu': 'South Malé Atoll, Maldives',
  'niyama private islands': 'Dhaalu Atoll, Maldives',
  'lux south ari atoll': 'South Ari Atoll, Maldives',
  'constance halaveli': 'North Ari Atoll, Maldives',
  'constance moofushi': 'South Ari Atoll, Maldives',
  'hideaway beach resort': 'Haa Alifu Atoll, Maldives',
  'gili lankanfushi': 'North Malé Atoll, Maldives',
  'waldorf astoria maldives': 'South Malé Atoll, Maldives',
  'st regis maldives': 'Dhaalu Atoll, Maldives',
  'w maldives': 'North Ari Atoll, Maldives',
  // Paris hotels
  'hotel georges v': 'Paris, France',
  'george v': 'Paris, France',
  'four seasons george v': 'Paris, France',
  'four seasons paris': 'Paris, France',
  'the ritz paris': 'Paris, France',
  'ritz paris': 'Paris, France',
  'plaza athenee': 'Paris, France',
  'le bristol': 'Paris, France',
  'le meurice': 'Paris, France',
  'hotel costes': 'Paris, France',
  'park hyatt paris': 'Paris, France',
  'shangri-la paris': 'Paris, France',
  // London hotels
  'the peninsula london': 'London, UK',
  'peninsula london': 'London, UK',
  'the savoy': 'London, UK',
  'claridges': 'London, UK',
  "claridge's": 'London, UK',
  'the dorchester': 'London, UK',
  'the connaught': 'London, UK',
  'the ned': 'London, UK',
  'the berkeley': 'London, UK',
  'the langham london': 'London, UK',
  'four seasons london': 'London, UK',
  // New York hotels
  'the plaza': 'New York City',
  'the carlyle': 'New York City',
  'the mark': 'New York City',
  'pierre hotel': 'New York City',
  'st regis new york': 'New York City',
  'four seasons new york': 'New York City',
  'the greenwich hotel': 'New York City',
  'crosby street hotel': 'New York City',
  // Italy hotels
  'belmond hotel cipriani': 'Venice, Italy',
  'hotel cipriani': 'Venice, Italy',
  'the gritti palace': 'Venice, Italy',
  'belmond villa san michele': 'Florence, Italy',
  'four seasons florence': 'Florence, Italy',
  'hotel hassler': 'Rome, Italy',
  'hotel de russie': 'Rome, Italy',
  'le sirenuse': 'Positano, Italy',
  'belmond caruso': 'Ravello, Italy',
  // Dubai/Middle East
  'burj al arab': 'Dubai, UAE',
  'atlantis the palm': 'Dubai, UAE',
  'atlantis the royal': 'Dubai, UAE',
  'jumeirah al naseem': 'Dubai, UAE',
  'one&only the palm': 'Dubai, UAE',
  'four seasons dubai': 'Dubai, UAE',
  'al maha desert resort': 'Dubai, UAE',
  'qasr al sarab': 'Abu Dhabi, UAE',
  // Fiji
  'likuliku lagoon': 'Fiji',
  'likuliku lagoon resort': 'Fiji',
  'kokomo private island': 'Fiji',
  'kokomo island': 'Fiji',
  'six senses fiji': 'Fiji',
  'laucala island': 'Fiji',
  'namale resort': 'Fiji',
  'tokoriki island resort': 'Fiji',
  'vomo island': 'Fiji',
  // Seychelles
  'north island': 'Seychelles',
  'north island seychelles': 'Seychelles',
  'four seasons seychelles': 'Seychelles',
  'six senses zil pasyon': 'Seychelles',
  'raffles seychelles': 'Seychelles',
  // Caribbean
  'beaches turks and caicos': 'Turks and Caicos',
  'beaches turks & caicos': 'Turks and Caicos',
  'sandals royal barbados': 'Barbados',
  'sandals grenada': 'Grenada',
  'half moon': 'Jamaica',
  'round hill': 'Jamaica',
  'goldeneye': 'Jamaica',
  // Ski/Mountain
  'chalet zermatt peak': 'Zermatt, Switzerland',
  'the chedi andermatt': 'Andermatt, Switzerland',
  'badrutts palace': 'St. Moritz, Switzerland',
  "badrutt's palace": 'St. Moritz, Switzerland',
  'hotel arlberg': 'Lech, Austria',
  'four seasons megeve': 'Megève, France',
  'four seasons megève': 'Megève, France',
  'aman le mélézin': 'Courchevel, France',
  // Asia-Pacific
  'como shambhala': 'Bali, Indonesia',
  'the mulia': 'Bali, Indonesia',
  'four seasons bali': 'Bali, Indonesia',
  'ayana resort': 'Bali, Indonesia',
  'alila villas uluwatu': 'Bali, Indonesia',
  'hoshinoya': 'Japan',
  'aman tokyo': 'Tokyo, Japan',
  'park hyatt tokyo': 'Tokyo, Japan',
  // Africa
  'singita grumeti': 'Tanzania',
  'singita kruger': 'South Africa',
  'royal malewane': 'South Africa',
  'londolozi': 'South Africa',
  'sabi sabi': 'South Africa',
  'grootbos': 'South Africa',
  'bisate lodge': 'Rwanda',
  'one&only cape town': 'Cape Town, South Africa',
  // Mexico/Central America
  'one&only mandarina': 'Riviera Nayarit, Mexico',
  'four seasons punta mita': 'Punta Mita, Mexico',
  'rosewood mayakoba': 'Riviera Maya, Mexico',
  'banyan tree mayakoba': 'Riviera Maya, Mexico',
  'nayara springs': 'Arenal, Costa Rica',
  'nayara gardens': 'Arenal, Costa Rica',
  'four seasons costa rica': 'Guanacaste, Costa Rica',
};

// ─── Airport codes & abbreviations ──────────────────────────────────────────

export const AIRPORT_CODES = new Set([
  'JFK', 'LAX', 'CDG', 'LHR', 'NRT', 'HND', 'SFO', 'ORD', 'DXB', 'SIN', 'HKG',
  'ICN', 'PEK', 'PVG', 'AMS', 'FRA', 'MUC', 'ZRH', 'BCN', 'MAD', 'FCO', 'MXP',
  'LGW', 'STN', 'MAN', 'EDI', 'DUB', 'BKK', 'KUL', 'CGK', 'MEL', 'SYD', 'AKL',
  'YYZ', 'YVR', 'YUL', 'MIA', 'ATL', 'DEN', 'DFW', 'SEA', 'BOS', 'EWR', 'IAD',
  'PHX', 'LAS', 'SAN', 'PDX', 'MSP', 'DTW', 'CLT', 'ORY', 'LIS', 'OPO', 'ATH',
  'IST', 'CAI', 'JNB', 'CPT', 'NBO', 'DEL', 'BOM', 'MAA', 'BLR', 'HYD', 'GRU',
  'GIG', 'EZE', 'SCL', 'LIM', 'BOG', 'MEX', 'CUN', 'SJU', 'NAS', 'MBJ',
]);

// Common abbreviations that might look like proper nouns but aren't destinations
export const COMMON_ABBREVIATIONS = new Set([
  'NYC', 'LA', 'SF', 'DC', 'TX', 'CA', 'NY', 'FL', 'NV', 'AZ', 'CO', 'WA', 'OR',
  'MA', 'IL', 'PA', 'OH', 'MI', 'GA', 'NC', 'NJ', 'VA', 'TN', 'MD', 'MN', 'MO',
]);

// Combine all known locations for validation
export const ALL_KNOWN_LOCATIONS = [...DESTINATION_CITIES, ...KNOWN_COUNTRIES, ...KNOWN_REGIONS, ...KNOWN_HOTELS_RESORTS];

// Words that are NEVER valid destinations (pronouns, generics, common words)
export const INVALID_DESTINATION_WORDS = new Set([
  // Pronouns
  'there', 'here', 'them', 'they', 'it', 'this', 'that', 'these', 'those',
  'somewhere', 'anywhere', 'everywhere', 'nowhere', 'wherever',
  // Generic terms
  'beach', 'beaches', 'mountain', 'mountains', 'city', 'cities', 'town', 'towns',
  'island', 'islands', 'village', 'villages', 'countryside', 'coast', 'coastal',
  'destination', 'destinations', 'place', 'places', 'location', 'locations',
  'vacation', 'trip', 'holiday', 'getaway', 'escape', 'adventure',
  // Descriptors
  'tropical', 'exotic', 'romantic', 'relaxing', 'beautiful', 'amazing', 'perfect',
  'warm', 'cold', 'sunny', 'snowy', 'rainy', 'hot', 'cool',
  // Activity types
  'hiking', 'skiing', 'surfing', 'diving', 'snorkeling', 'camping', 'fishing',
  // Common words that might appear
  'one', 'two', 'first', 'second', 'next', 'last', 'new', 'old',
  'great', 'good', 'best', 'nice', 'cool', 'fun', 'awesome',
  // Commands/actions
  'go', 'visit', 'explore', 'see', 'check', 'try', 'find', 'look',
]);

// ─── Detection & validation functions ───────────────────────────────────────

// Extract location from hotel name for itinerary generation (sync version)
// Uses static map first, then pattern matching
export function extractLocationFromHotelName(name) {
  if (!name) return name;

  const nameLower = name.toLowerCase().trim();

  // Check direct mapping first
  if (HOTEL_LOCATION_MAP[nameLower]) {
    return HOTEL_LOCATION_MAP[nameLower];
  }

  // BUG 11 FIX: Check partial matches sorted by key length DESCENDING for specificity
  // e.g., "singita sabi sand" should match 'singita sabi sand' (specific) before 'singita' (generic)
  // Only match if input contains key OR key contains input with minimum length threshold
  const sortedKeys = Object.keys(HOTEL_LOCATION_MAP).sort((a, b) => b.length - a.length);
  for (const hotelKey of sortedKeys) {
    if (nameLower.includes(hotelKey)) {
      return HOTEL_LOCATION_MAP[hotelKey];
    }
    // Reverse match: only if input is long enough to be a meaningful hotel name (>= 6 chars)
    // This prevents "aman" → "amangiri" → Utah, "como" → "como parrot cay" → Turks and Caicos
    if (nameLower.length >= 6 && hotelKey.includes(nameLower)) {
      return HOTEL_LOCATION_MAP[hotelKey];
    }
  }

  // Try to extract location from "Brand Location" pattern
  // e.g., "Four Seasons Bali" -> "Bali", "Park Hyatt Tokyo" -> "Tokyo"
  const brandPrefixes = [
    'four seasons', 'ritz carlton', 'the ritz-carlton', 'mandarin oriental', 'park hyatt',
    'st regis', 'st. regis', 'waldorf astoria', 'aman', 'one&only', 'six senses',
    'rosewood', 'peninsula', 'the peninsula', 'raffles', 'belmond', 'como',
    'banyan tree', 'shangri-la', 'fairmont', 'sofitel', 'nobu hotel',
    'edition', 'capella', 'auberge', 'soneva', 'andaz', 'w hotel', 'w ',
    'jw marriott', 'oberoi', 'the oberoi', 'taj', 'leela',
    'intercontinental', 'hyatt regency', 'grand hyatt',
    'sandals', 'beaches', 'club med',
    'centara', 'anantara', 'lux', 'constance', 'hideaway', 'niyama',
    'baglioni', 'jumeirah', 'kempinski', 'melia', 'nikki beach',
  ];

  for (const brand of brandPrefixes) {
    if (nameLower.startsWith(brand)) {
      let afterBrand = name.slice(brand.length).trim();
      // Strip leading punctuation (comma, dash) from "Four Seasons, Bali" → "Bali"
      afterBrand = afterBrand.replace(/^[,\-–—:]\s*/, '').trim();
      // Strip generic hospitality words that aren't geographic
      // e.g., "Belmond Hotel Splendido" → "Hotel Splendido" → skip (not a location)
      // but "Four Seasons Bali" → "Bali" → keep
      afterBrand = afterBrand.replace(/^(hotel|resort|inn|lodge|spa|villa|palace|suites?|mansion|house)\s+/i, '').trim();
      if (afterBrand.length > 2) {
        return afterBrand;
      }
    }
  }

  // If name contains comma, ONLY extract location if the first part looks like a hotel/property name
  // e.g., "Hotel Splendido, Portofino" → "Portofino" (first part has hotel keyword)
  // e.g., "Santorini, Greece" → keep as-is (first part is just a place name)
  // e.g., "Tuscany, Italy" → keep as-is (no hotel signal)
  if (name.includes(',')) {
    const parts = name.split(',');
    if (parts.length >= 2 && parts[1].trim().length > 2) {
      const firstPart = parts[0].trim().toLowerCase();
      // Only extract if the part BEFORE the comma contains a hotel/property signal
      const hotelKeywords = ['hotel', 'resort', 'inn', 'lodge', 'palace', 'villa', 'house',
        'manor', 'spa', 'ranch', 'farm', 'suites', 'château', 'chateau', 'mansion',
        'boutique', 'hostel', 'motel', 'guesthouse', 'bed and breakfast', 'b&b',
        'residences', 'towers', 'clubhouse'];
      const hotelBrands = ['four seasons', 'ritz', 'mandarin', 'peninsula', 'rosewood',
        'six senses', 'belmond', 'como', 'aman', 'one&only', 'raffles', 'waldorf',
        'st regis', 'park hyatt', 'soneva', 'banyan tree', 'shangri-la', 'fairmont',
        'sofitel', 'nobu', 'edition', 'capella', 'auberge'];
      const hasHotelSignal = hotelKeywords.some(kw => firstPart.includes(kw)) ||
        hotelBrands.some(brand => firstPart.includes(brand));
      if (hasHotelSignal) {
        return parts[1].trim();
      }
      // No hotel signal → this is likely "City, Country" format → return as-is
    }
  }

  // Fallback: return original name (async lookup will be used separately)
  return name;
}

// Check if message is asking for a hotel recommendation rather than naming a specific hotel
export function isHotelRecommendationRequest(input) {
  if (!input || typeof input !== 'string') return false;

  // First check explicit recommendation patterns
  if (HOTEL_RECOMMENDATION_PATTERNS.some(pattern => pattern.test(input))) {
    return true;
  }

  // Check for generic "article + adjective + accommodation" without a location
  // e.g., "I want to go to a nice hotel" (generic) vs "I want a nice hotel in Paris" (has location)
  if (GENERIC_ACCOMMODATION_PATTERN.test(input)) {
    // If there's a location specified (in/at/near + place), it's more specific but still a recommendation
    const hasLocation = /\b(in|at|near|around)\s+[A-Z][a-zA-Z]+/i.test(input);
    // If no location, this is definitely a generic request
    if (!hasLocation) {
      return true;
    }
  }

  return false;
}

export function isAmbiguousDestination(userInput, extractedResult) {
  if (!extractedResult) return false;

  const inputLower = userInput.toLowerCase();
  const matchLower = extractedResult.name.toLowerCase();

  // Pattern 1: "nice hotel" ambiguity - could be Nice city or "nice" adjective
  if (matchLower.includes('nice') && /\bnice\s+(hotel|resort|place)/i.test(inputLower)) {
    return true;
  }

  // Pattern 2: Generic article + adjective + accommodation
  if (GENERIC_ACCOMMODATION_PATTERN.test(inputLower)) {
    return true;
  }

  // Pattern 3: Low confidence results
  if (extractedResult.confidence === 'low') {
    return true;
  }

  // Pattern 4: Needs confirmation flag
  if (extractedResult.needsConfirmation) {
    return true;
  }

  return false;
}

// Find best fuzzy match for a potentially misspelled city
export function fuzzyMatchCity(input, maxDistance = 2) {
  if (!input || input.length < 3) return null;

  const inputLower = input.toLowerCase().trim();

  // Check for exact match first
  const exactMatch = DESTINATION_CITIES.find(c => c.toLowerCase() === inputLower);
  if (exactMatch) return { match: exactMatch, distance: 0, isExact: true };

  // Find closest matches within maxDistance
  let bestMatch = null;
  let bestDistance = maxDistance + 1;

  for (const city of DESTINATION_CITIES) {
    const distance = levenshteinDistance(input, city);

    // Adjust threshold based on word length (longer words tolerate more errors)
    const threshold = Math.min(maxDistance, Math.floor(city.length / 3));

    if (distance <= threshold && distance < bestDistance) {
      bestDistance = distance;
      bestMatch = city;
    }
  }

  return bestMatch ? { match: bestMatch, distance: bestDistance, isExact: false } : null;
}

// Validate if a string is a real destination (city, country, region, hotel, or resort)
// Returns: { valid, match, confidence, type, needsConfirmation } or false
export function isRealDestination(input, originalMessage = null) {
  if (!input || typeof input !== 'string') return false;

  const trimmed = input.trim();
  const cleaned = trimmed.toLowerCase();

  // Too short to be a real place (but allow 2-letter country codes like UK, US)
  if (cleaned.length < 2) return false;

  // Check if it's an airport code - these are NOT destinations
  if (AIRPORT_CODES.has(trimmed.toUpperCase())) return false;

  // Check if it's a common abbreviation (NYC, LA, SF) - these need mapping
  if (COMMON_ABBREVIATIONS.has(trimmed.toUpperCase())) {
    // Map common abbreviations to real destinations
    const abbrevMap = { 'NYC': 'New York', 'LA': 'Los Angeles', 'SF': 'San Francisco', 'DC': 'Washington DC' };
    const mapped = abbrevMap[trimmed.toUpperCase()];
    if (mapped) {
      return { valid: true, match: mapped, confidence: 'high', type: 'location', needsConfirmation: false };
    }
    return false; // Unknown abbreviation
  }

  // Check if it's an invalid word
  if (INVALID_DESTINATION_WORDS.has(cleaned)) return false;

  // Check if input contains numbers (e.g., "paris123") - reject unless it's a known location
  if (/\d/.test(trimmed)) {
    // Only allow if it's an exact match to a known location
    const exactWithNumbers = ALL_KNOWN_LOCATIONS.find(loc => loc.toLowerCase() === cleaned);
    if (!exactWithNumbers) return false;
  }

  // Check for exact match in known locations (case-insensitive)
  const exactMatch = ALL_KNOWN_LOCATIONS.find(loc =>
    loc.toLowerCase() === cleaned ||
    cleaned === loc.toLowerCase().replace(/\s+/g, '') // Handle "newyork" -> "new york"
  );
  if (exactMatch) {
    // Determine if it's a hotel/resort
    const isHotelResort = KNOWN_HOTELS_RESORTS.some(h => h.toLowerCase() === exactMatch.toLowerCase());
    return { valid: true, match: exactMatch, confidence: 'high', type: isHotelResort ? 'hotel' : 'location', needsConfirmation: false };
  }

  // Check if this is a hotel RECOMMENDATION request (not a specific hotel name)
  // e.g., "luxury hotel in Tokyo" or "best resort in Maldives"
  const messageToCheck = originalMessage || input;
  if (isHotelRecommendationRequest(messageToCheck)) {
    // Extract the location from the recommendation request
    // e.g., "luxury hotel in Tokyo" -> we want "Tokyo", not the whole phrase
    // Also try to match "where should I stay in Rome?"
    const locationPatterns = [
      /\b(?:in|near|around|at)\s+([A-Z][a-zA-Z\s]+?)(?:\s*$|\s*[,?.]|\s+(?:for|with|during))/i,
      /\bstay\s+(?:in|at)\s+([A-Z][a-zA-Z\s]+?)(?:\s*$|\s*[,?.])/i,
    ];
    for (const pattern of locationPatterns) {
      const locationMatch = messageToCheck.match(pattern);
      if (locationMatch) {
        const extractedLocation = locationMatch[1].trim();
        // Recursively validate just the location part
        const locationValidation = isRealDestination(extractedLocation);
        if (locationValidation && locationValidation.valid) {
          return locationValidation; // Return the actual location, not the recommendation phrase
        }
      }
    }
    // If we can't extract a valid location, this is a recommendation request, not a destination
    return false;
  }

  // Check for partial match - but be more strict to avoid "ab" matching "Casablanca"
  // Only match if the input is at least 3 chars AND either:
  //   - Input contains a full known location word, OR
  //   - A known location starts with the input (prefix match)
  if (cleaned.length >= 3) {
    // First, check if input IS a known hotel brand at the start of a longer string
    // e.g., "Four Seasons Maui" should match "Four Seasons" as a hotel
    const hotelBrandMatch = KNOWN_HOTELS_RESORTS.find(hotel => {
      const hotelLower = hotel.toLowerCase();
      return cleaned.startsWith(hotelLower + ' ') || cleaned === hotelLower;
    });
    if (hotelBrandMatch) {
      return { valid: true, match: hotelBrandMatch, confidence: 'high', type: 'hotel', needsConfirmation: false };
    }

    // Check for location contained in input (e.g., "Ritz Carlton Bali" contains "Bali")
    // But prefer the hotel brand if one exists
    const locationInInput = DESTINATION_CITIES.concat(KNOWN_COUNTRIES, KNOWN_REGIONS).find(loc => {
      const locLower = loc.toLowerCase();
      // Only match if it's a word boundary match
      const regex = new RegExp(`\\b${locLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      return regex.test(cleaned);
    });

    // If the input contains BOTH a hotel keyword AND a location, treat as hotel with location
    const containsHotelKeyword = HOTEL_RESORT_KEYWORDS.some(keyword =>
      cleaned.includes(keyword.toLowerCase())
    );
    if (locationInInput && containsHotelKeyword) {
      // This is like "Hyatt Regency Maui" - a hotel at a location
      return { valid: true, match: trimmed, confidence: 'medium', type: 'hotel', needsConfirmation: true };
    }

    // If just a location match with no hotel context, return the location
    if (locationInInput && locationInInput.length >= cleaned.length * 0.5) {
      return { valid: true, match: locationInInput, confidence: 'medium', type: 'location', needsConfirmation: false };
    }
  }

  // Check if it contains hotel/resort keywords (for properties not in our list)
  // e.g., "Hyatt Regency Maui" or "Bellagio Las Vegas"
  const containsHotelKeyword = HOTEL_RESORT_KEYWORDS.some(keyword =>
    cleaned.includes(keyword.toLowerCase())
  );
  if (containsHotelKeyword && trimmed.length >= 5) {
    // Looks like a hotel/resort name, but it's not in our list
    // Mark as needing confirmation since we're not sure it's real
    return { valid: true, match: trimmed, confidence: 'low', type: 'hotel', needsConfirmation: true };
  }

  // Try fuzzy matching for typos
  const fuzzyResult = fuzzyMatchCity(input, 2);
  if (fuzzyResult && fuzzyResult.match) {
    return { valid: true, match: fuzzyResult.match, confidence: fuzzyResult.isExact ? 'high' : 'medium', type: 'location', needsConfirmation: false };
  }

  // If not found in our lists, check if it LOOKS like a proper noun (capitalized)
  // and doesn't contain invalid words
  const words = trimmed.split(/\s+/);
  const looksLikeProperNoun = words.every(word =>
    word.length > 0 && /^[A-Z]/.test(word) && !INVALID_DESTINATION_WORDS.has(word.toLowerCase())
  );

  // More strict: require at least 3 chars and not all caps (to avoid "JFK", "LAX" style)
  const isAllCaps = trimmed === trimmed.toUpperCase() && trimmed.length <= 4;

  // Plausibility: single words must contain a vowel and be >= 4 chars
  // Nonsense like "Lbd", "Xyz", "Qkr" fail this. Multi-word names like "Sri Lanka" pass.
  const VOWELS = /[aeiouyàáâãäåæèéêëìíîïòóôõöùúûüýÿ]/i;
  const isSingleWordNonsense = words.length === 1 && (
    !VOWELS.test(cleaned) ||   // No vowels → not a real place name
    cleaned.length < 4          // Too short for unknown single-word places
  );

  if (looksLikeProperNoun && words.length <= 5 && !isAllCaps && trimmed.length >= 3 && !isSingleWordNonsense) {
    // Could be a valid destination or hotel we don't know about
    // Check if any word suggests it's a hotel/resort
    const mightBeHotel = words.some(word =>
      HOTEL_RESORT_KEYWORDS.includes(word.toLowerCase())
    );
    // Mark as needing confirmation since it's not in our lists
    return { valid: true, match: trimmed, confidence: 'low', type: mightBeHotel ? 'hotel' : 'location', needsConfirmation: true };
  }

  return false;
}

// ─── Hotel/resort detection ─────────────────────────────────────────────────

// Hotel brand prefixes for detecting hotel names (e.g., "Aman Utah", "Four Seasons Maui")
export const HOTEL_BRAND_PREFIXES = [
  'four seasons', 'ritz carlton', 'the ritz-carlton', 'mandarin oriental', 'park hyatt',
  'st regis', 'st. regis', 'waldorf astoria', 'aman', 'one&only', 'six senses',
  'rosewood', 'peninsula', 'the peninsula', 'raffles', 'belmond', 'como',
  'banyan tree', 'shangri-la', 'fairmont', 'sofitel', 'nobu hotel',
  'edition', 'capella', 'auberge', 'soneva', 'andaz', 'w hotel', 'w ',
  'jw marriott', 'oberoi', 'the oberoi', 'taj', 'leela',
  'intercontinental', 'hyatt regency', 'grand hyatt', 'conrad',
  'sandals', 'beaches', 'club med',
  // Single-word brand names that produce short "Brand Resort" / "Brand Hotel"
  // inputs that the isShortCitySuffix guard would otherwise misclassify as
  // cities (audit found "Atlantis Resort" landed as a city not a resort).
  'atlantis', 'paradisus', 'iberostar', 'riu', 'secrets',
];

export function isHotelOrResort(name) {
  if (!name) return false;
  const nameLower = name.toLowerCase().trim();

  // Check if it's in our hotel location map (most specific)
  if (HOTEL_LOCATION_MAP[nameLower]) return true;

  // Check against known hotels/resorts list — match with WORD BOUNDARIES on
  // the substring check, otherwise short brand names like "Riu" / "Aman" /
  // "Taj" match inside unrelated city names ("Triumph" contains "riu",
  // "Mauritius" contains "tius", etc.). Without word boundaries, the audit
  // found "Triumph" / "Triumph Tower" / "Riumar" all returning true.
  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const isKnownHotel = KNOWN_HOTELS_RESORTS.some(hotel => {
    const hotelLower = hotel.toLowerCase();
    if (nameLower === hotelLower) return true;
    if (nameLower.startsWith(hotelLower + ' ')) return true;
    // Word-boundary substring: "The Aman Resort Tokyo" matches Aman, but
    // "Triumph" doesn't match Riu (riu is mid-word, no boundary).
    return new RegExp(`\\b${escapeRe(hotelLower)}\\b`, 'i').test(nameLower);
  });
  if (isKnownHotel) return true;

  // Check for hotel brand patterns. Use word-boundary at the start so single-
  // word brands ('atlantis', 'riu') only match when the brand stands as its
  // own token — "Atlantis Resort" matches 'atlantis', but "atlantistic" or
  // "Triumph" (containing 'riu') won't.
  const hasHotelPrefix = HOTEL_BRAND_PREFIXES.some(prefix => {
    const re = new RegExp(`^${escapeRe(prefix)}\\b`, 'i');
    return re.test(nameLower);
  });
  if (hasHotelPrefix) return true;

  // FALSE-POSITIVE GUARD: short "City + suffix" or "suffix + City" inputs
  // like "Tokyo Hotel" / "Inn Tokyo" / "London Inn" are ambiguous — the
  // user almost always means the city. We only treat as a hotel if there's
  // evidence beyond city + suffix: 3+ tokens, a known brand keyword, or
  // the suffix appears mid-string.
  //
  // NARROWER than round 6: only suppress when the OTHER token is a known
  // travel city. Otherwise generic "Hotel California" / "Hotel Plaza" /
  // "Hotel Roma" stay classified as hotels (round 6 incorrectly turned
  // these false). The fix is now: "Hotel Tokyo" → city (Tokyo IS a city);
  // "Hotel California" → hotel (California is not in our city set).
  const SUFFIX_WORD = /^(hotel|resort|lodge|inn|spa|villa|retreat)$/i;
  const tokens = nameLower.split(/\s+/).filter(Boolean);
  const isCity = (tok) => KNOWN_TRAVEL_CITIES.has(tok.trim().toLowerCase());
  const isShortCitySuffix = tokens.length === 2 && (
    (SUFFIX_WORD.test(tokens[1]) && isCity(tokens[0])) || // "Tokyo Hotel"
    (SUFFIX_WORD.test(tokens[0]) && isCity(tokens[1]))    // "Hotel Tokyo"
  );

  // Check for "Hotel X" or "The X Hotel" patterns (e.g., "Hotel Georges V", "The Plaza Hotel")
  if (/^(?:the\s+)?hotel\s+\w/i.test(nameLower) || /\bhotel$/i.test(nameLower)) {
    if (!isShortCitySuffix) return true;
  }

  // Generic: name contains "hotel", "resort", "lodge", or "inn" as a word
  // e.g., "Waldhaus Flims Alpine Grand Hotel & Spa in Switzerland"
  if (/\b(hotel|resort|lodge|inn)\b/i.test(nameLower)) {
    if (!isShortCitySuffix) return true;
  }

  // Check for "The [Brand] [Location]" pattern (e.g., "The Peninsula London", "The Ritz Paris")
  if (/^the\s+\w+\s+\w+/i.test(nameLower)) {
    // Check if any known brand is in the name
    const brandKeywords = ['peninsula', 'ritz', 'plaza', 'savoy', 'dorchester', 'connaught',
      'carlyle', 'mark', 'ned', 'hoxton', 'standard', 'edition'];
    if (brandKeywords.some(brand => nameLower.includes(brand))) {
      return true;
    }
  }

  return false;
}

// ─── Seasonal recommendations ───────────────────────────────────────────────

export function getRecommendedMonths(destination) {
  if (!destination) return [];
  const destLower = destination.toLowerCase();

  // Hemisphere-based defaults
  const southernHemisphere = ['australia', 'new zealand', 'argentina', 'chile', 'south africa', 'patagonia', 'buenos aires', 'cape town', 'sydney', 'melbourne', 'queenstown'];
  const isSouthern = southernHemisphere.some(place => destLower.includes(place));

  // Specific destination recommendations
  const recommendations = {
    // Europe
    'paris': ['April', 'May', 'June', 'September', 'October'],
    'rome': ['April', 'May', 'September', 'October'],
    'barcelona': ['May', 'June', 'September', 'October'],
    'greece': ['May', 'June', 'September', 'October'],
    'santorini': ['May', 'June', 'September', 'October'],
    'iceland': ['June', 'July', 'August'],
    'norway': ['June', 'July', 'August'],
    'switzerland': ['June', 'July', 'August', 'September', 'December', 'January', 'February'],
    'alps': ['June', 'July', 'August', 'December', 'January', 'February'],
    'portugal': ['April', 'May', 'June', 'September', 'October'],
    'croatia': ['May', 'June', 'September', 'October'],
    'turkey': ['April', 'May', 'September', 'October'],
    'spain': ['April', 'May', 'June', 'September', 'October'],
    'italy': ['April', 'May', 'September', 'October'],
    'france': ['April', 'May', 'June', 'September', 'October'],

    // Asia
    'japan': ['March', 'April', 'October', 'November'],
    'tokyo': ['March', 'April', 'October', 'November'],
    'kyoto': ['March', 'April', 'October', 'November'],
    'thailand': ['November', 'December', 'January', 'February', 'March'],
    'bangkok': ['November', 'December', 'January', 'February'],
    'bali': ['April', 'May', 'June', 'July', 'August', 'September'],
    'vietnam': ['February', 'March', 'April'],
    'india': ['October', 'November', 'December', 'January', 'February', 'March'],
    'nepal': ['October', 'November', 'March', 'April', 'May'],
    'kathmandu': ['October', 'November', 'March', 'April', 'May'],
    'bhutan': ['March', 'April', 'May', 'September', 'October', 'November'],
    'sri lanka': ['January', 'February', 'March', 'April', 'July', 'August'],
    'china': ['April', 'May', 'September', 'October'],
    'korea': ['April', 'May', 'September', 'October', 'November'],
    'singapore': ['February', 'March', 'April', 'May', 'June', 'July'],
    'malaysia': ['March', 'April', 'May', 'September', 'October'],
    'indonesia': ['April', 'May', 'June', 'July', 'August', 'September'],
    'philippines': ['December', 'January', 'February', 'March', 'April', 'May'],
    'cambodia': ['November', 'December', 'January', 'February', 'March'],

    // Americas
    'peru': ['May', 'June', 'July', 'August', 'September'],
    'machu picchu': ['May', 'June', 'July', 'August', 'September'],
    'patagonia': ['November', 'December', 'January', 'February', 'March'],
    'costa rica': ['December', 'January', 'February', 'March', 'April'],
    'mexico': ['November', 'December', 'January', 'February', 'March', 'April'],
    'caribbean': ['December', 'January', 'February', 'March', 'April'],
    'brazil': ['May', 'June', 'July', 'August', 'September'],
    'colombia': ['December', 'January', 'February', 'March'],
    'argentina': ['October', 'November', 'December', 'January', 'February', 'March'],
    'chile': ['October', 'November', 'December', 'January', 'February', 'March'],
    'ecuador': ['June', 'July', 'August', 'September'],

    // Africa & Middle East
    'morocco': ['March', 'April', 'May', 'October', 'November'],
    'safari': ['June', 'July', 'August', 'September', 'October'],
    'kenya': ['June', 'July', 'August', 'September', 'October'],
    'tanzania': ['June', 'July', 'August', 'September', 'October'],
    'egypt': ['October', 'November', 'December', 'January', 'February', 'March'],
    'dubai': ['November', 'December', 'January', 'February', 'March'],
    'south africa': ['September', 'October', 'November', 'March', 'April', 'May'],
    'jordan': ['March', 'April', 'May', 'September', 'October', 'November'],
    'israel': ['March', 'April', 'May', 'September', 'October', 'November'],

    // Oceania
    'australia': ['September', 'October', 'November', 'March', 'April', 'May'],
    'new zealand': ['December', 'January', 'February', 'March'],
    'fiji': ['May', 'June', 'July', 'August', 'September', 'October'],
    'maldives': ['January', 'February', 'March', 'April'],
    'bora bora': ['May', 'June', 'July', 'August', 'September', 'October'],
  };

  // For multi-stop trips, try to find common months between destinations
  // Parse destinations from trip name (e.g., "2-Stop Trip" or "Nepal, Bhutan")
  const { text: protDest, placeholders: destPH } = protectCompoundNames(destLower);
  const destinationParts = restoreCompoundNames(protDest.split(/[,&+]|\band\b|\bto\b/), destPH);

  // Check for specific destination match (single destination or first match)
  for (const [place, months] of Object.entries(recommendations)) {
    if (destLower.includes(place)) {
      return months;
    }
  }

  // Try parsing individual destinations for multi-stop
  if (destinationParts.length > 1) {
    const allMonthSets = [];
    for (const part of destinationParts) {
      const partTrimmed = part.trim();
      for (const [place, months] of Object.entries(recommendations)) {
        if (partTrimmed.includes(place)) {
          allMonthSets.push(months);
          break;
        }
      }
    }

    // Find intersection of recommended months
    if (allMonthSets.length > 0) {
      const intersection = allMonthSets.reduce((common, months) =>
        common.filter(m => months.includes(m)), allMonthSets[0]);
      if (intersection.length > 0) return intersection;
      // If no intersection, return first destination's recommendations
      return allMonthSets[0];
    }
  }

  // Default based on hemisphere
  if (isSouthern) {
    return ['November', 'December', 'January', 'February', 'March']; // Southern summer
  }

  // Northern hemisphere default (spring/fall are safest)
  return ['April', 'May', 'September', 'October'];
}
